import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Shell } from "../components/Shell";
import { ApiError, getToken, tenants } from "../lib/api";
import type { Tenant, TenantBranding } from "../lib/api";

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.mipuebloapp.com";

function useBlobImage(url: string | null): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!url) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let blobUrl: string | null = null;
    const token = getToken();
    fetch(url, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob && !cancelled) {
          blobUrl = URL.createObjectURL(blob);
          setSrc(blobUrl);
        }
      })
      .catch(() => setSrc(null));
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  return src;
}

export function TenantBrandingPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Form state
  const [colorPrimario, setColorPrimario] = useState("#0F4C81");
  const [firmaTitulo, setFirmaTitulo] = useState("");
  const [firmaNombre, setFirmaNombre] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([tenants.get(id), tenants.getBranding(id)])
      .then(([t, b]) => {
        setTenant(t);
        setBranding(b);
        setColorPrimario(b.color_primario ?? "#0F4C81");
        setFirmaTitulo(b.firma_titulo ?? "");
        setFirmaNombre(b.firma_nombre ?? "");
        setDireccion(b.direccion_institucional ?? "");
        setTelefono(b.telefono_institucional ?? "");
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "No se pudo cargar"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  const saveBranding = async () => {
    if (!id) return;
    setBusy("branding");
    try {
      const b = await tenants.updateBranding(id, {
        color_primario: colorPrimario,
        firma_titulo: firmaTitulo,
        firma_nombre: firmaNombre,
        direccion_institucional: direccion,
        telefono_institucional: telefono,
      });
      setBranding(b);
      flash("ok", "Identidad guardada");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "No se pudo guardar");
    } finally {
      setBusy(null);
    }
  };

  const upload = async (kind: "logo" | "escudo", file: File) => {
    if (!id) return;
    setBusy(kind);
    try {
      const b = await (kind === "logo"
        ? tenants.uploadLogo(id, file)
        : tenants.uploadEscudo(id, file));
      setBranding(b);
      flash("ok", `${kind === "logo" ? "Logo" : "Escudo"} cargado`);
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "No se pudo cargar");
    } finally {
      setBusy(null);
    }
  };

  // Hooks ANTES de cualquier early return (Rules of Hooks).
  const logoUrl =
    tenant && branding?.logo_path
      ? `${API_BASE}/admin/tenants/${tenant.id}/branding/image?slot=logo&v=${encodeURIComponent(branding.logo_path)}`
      : null;
  const escudoUrl =
    tenant && branding?.escudo_path
      ? `${API_BASE}/admin/tenants/${tenant.id}/branding/image?slot=escudo&v=${encodeURIComponent(branding.escudo_path)}`
      : null;
  const logoSrc = useBlobImage(logoUrl);
  const escudoSrc = useBlobImage(escudoUrl);

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !tenant) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "Tenant no encontrado."}</div>
          <p>
            <Link to="/tenants">← Volver</Link>
          </p>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to={`/tenants/${tenant.id}`}>← {tenant.nombre}</Link> · Identidad institucional
      </div>
      <h1 style={{ fontSize: 28, marginTop: 6 }}>Membrete del PDF oficial</h1>
      <p className="lede" style={{ maxWidth: 640 }}>
        Estos datos aparecen en el encabezado y firma del PDF que se adjunta
        a cada respuesta de PQRSDF.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          marginTop: 24,
        }}
      >
        {/* Logo */}
        <div className="card">
          <div className="card-inner">
            <div className="eyebrow">Logo institucional</div>
            <p className="helper">PNG/JPG/WebP/SVG · máx 2 MB</p>
            <div className="branding-preview">
              {logoSrc ? (
                <img src={logoSrc} alt="Logo" />
              ) : (
                <span className="branding-empty">Sin logo</span>
              )}
            </div>
            <label className="cta ghost" style={{ marginTop: 12, maxWidth: 220 }}>
              <span className="cta-inner">
                {busy === "logo" ? "Subiendo…" : "Cargar logo"}
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                style={{ display: "none" }}
                disabled={busy === "logo"}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void upload("logo", f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        </div>

        {/* Escudo */}
        <div className="card">
          <div className="card-inner">
            <div className="eyebrow">Escudo municipal</div>
            <p className="helper">PNG/JPG/WebP/SVG · máx 2 MB</p>
            <div className="branding-preview">
              {escudoSrc ? (
                <img src={escudoSrc} alt="Escudo" />
              ) : (
                <span className="branding-empty">Sin escudo</span>
              )}
            </div>
            <label className="cta ghost" style={{ marginTop: 12, maxWidth: 220 }}>
              <span className="cta-inner">
                {busy === "escudo" ? "Subiendo…" : "Cargar escudo"}
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                style={{ display: "none" }}
                disabled={busy === "escudo"}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void upload("escudo", f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-inner">
          <div className="eyebrow">Datos del oficio</div>

          <div className="form-row">
            <label className="label">Color primario (hex)</label>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                type="color"
                value={colorPrimario}
                onChange={(e) => setColorPrimario(e.target.value)}
                style={{ width: 48, height: 38, border: "1px solid var(--hairline)" }}
              />
              <input
                type="text"
                className="input"
                value={colorPrimario}
                onChange={(e) => setColorPrimario(e.target.value)}
                pattern="^#[0-9A-Fa-f]{6}$"
                placeholder="#0F4C81"
                style={{ maxWidth: 160 }}
              />
            </div>
          </div>

          <div className="form-row">
            <label className="label">Nombre de quien firma</label>
            <input
              type="text"
              className="input"
              value={firmaNombre}
              onChange={(e) => setFirmaNombre(e.target.value)}
              placeholder="Juana Pérez García"
              maxLength={128}
            />
          </div>

          <div className="form-row">
            <label className="label">Cargo de quien firma</label>
            <input
              type="text"
              className="input"
              value={firmaTitulo}
              onChange={(e) => setFirmaTitulo(e.target.value)}
              placeholder="Secretaria General"
              maxLength={128}
            />
          </div>

          <div className="form-row">
            <label className="label">Dirección institucional</label>
            <input
              type="text"
              className="input"
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              placeholder="Cra. 5 # 5-50, Centro"
              maxLength={256}
            />
          </div>

          <div className="form-row">
            <label className="label">Teléfono institucional</label>
            <input
              type="text"
              className="input"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="+57 1 234 5678"
              maxLength={64}
            />
          </div>

          <button
            type="button"
            className="cta"
            disabled={busy === "branding"}
            onClick={() => void saveBranding()}
            style={{ maxWidth: 260, marginTop: 10 }}
          >
            <span className="cta-inner">
              {busy === "branding" ? "Guardando…" : "Guardar identidad"}
            </span>
          </button>
        </div>
      </div>

        {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
      </section>
    </Shell>
  );
}
